import React, { Fragment, useState, useEffect } from 'react';
import { Chip, FormControl, Popover, TextField } from '@material-ui/core';
import dayjs from 'dayjs';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { MenuItemPopOver, TitlePopOver } from 'modules/Sty';
import { convertSnakeCase, makeKey } from 'modules/Utils';
import { FilterTypeDate } from 'pages/MarketingPage/Components/CampaignTypesFilter/CampaignFilterTypes';
import DropdownArrow from 'components/shareable/DropdownArrow';
import ButtonSymphony from 'elements/ButtonSymphony';
import useScreen from 'Hooks/useScreen';
import useBrand, { BrandExistsState } from 'Hooks/useBrand';
import useAppView from 'Hooks/useAppView';
import useOrganizationV2 from 'Hooks/useOrganizationV2';
import Icon from 'elements/Icon';
import CustomAvatar from 'components/ProfileDropdown/CustomAvatar';
import { CurrentBrand } from 'modules/Types';
import Colors from 'modules/Colors';
import DatePickerSymphony from 'elements/DatePickerSymphony';

// The filter options available for invoices
const INVOICE_FILTERS = [
    { name: 'date', title: 'Date' },
    { name: 'amount', title: 'Amount' },
];

// Additional filter for team view
const TEAM_INVOICE_FILTERS = [
    { name: 'brand', title: 'Brand' },
    ...INVOICE_FILTERS
];

type FilterTypeAmount = 'no_amount_filter' | 'equal' | 'greater_than' | 'less_than' | 'between_two_numbers';

interface InvoiceFilterOptionsProps {
    filter: {
        brands: string[];
        statuses: string[];
        dateModifier: FilterTypeDate;
        startDate: string;
        endDate: string;
        filterDays: string;
        amountModifier: FilterTypeAmount;
        amountValue1: string;
        amountValue2: string;
    };
    onFilterChange: (filter: any) => void;
    onApplyFilters: () => void;
    onResetFilters: () => void;
    isLoading?: boolean;
}

const InvoiceFilterOptions = ({
    filter,
    onFilterChange,
    onApplyFilters,
    onResetFilters,
    isLoading = false
}: InvoiceFilterOptionsProps) => {
    const { mobileView } = useScreen();
    const { isTeamView } = useAppView();
    const { organization } = useOrganizationV2();
    const { currentBrand, loadedBrandsDict } = useBrand() as BrandExistsState;

    const [openPopoverIndex, setOpenPopoverIndex] = useState<number | null>(null);
    const [selectedBrands, setSelectedBrands] = useState<number[]>([]);
    const [brandSearchText, setBrandSearchText] = useState<string>('');

    // Sync selectedBrands with filter.brands prop
    useEffect(() => {
        const brandIds = filter.brands.map(id => parseInt(id)).filter(id => !isNaN(id));
        setSelectedBrands(brandIds);
    }, [filter.brands]);

    const applyFilters = () => {
        handleClosePopover();
        
        // Update the filter with selected brands
        const updatedFilter = {
            ...filter,
            brands: selectedBrands.map(id => id.toString())
        };
        
        onFilterChange(updatedFilter);
        onApplyFilters();
    };

    const removeFilter = () => {
        const filtersToUse = isTeamView() ? TEAM_INVOICE_FILTERS : INVOICE_FILTERS;
        const filterIndex = openPopoverIndex!;
        const filterName = filtersToUse[filterIndex]?.name;

        let updatedFilter = { ...filter };

        switch (filterName) {
            case 'brand':
                setSelectedBrands([]);
                setBrandSearchText('');
                updatedFilter.brands = [];
                break;
            case 'date':
                updatedFilter.dateModifier = 'all_time';
                updatedFilter.startDate = dayjs().subtract(30, 'days').format('YYYY-MM-DD');
                updatedFilter.endDate = dayjs().format('YYYY-MM-DD');
                updatedFilter.filterDays = '30';
                break;
            case 'amount':
                updatedFilter.amountModifier = 'no_amount_filter';
                updatedFilter.amountValue1 = '';
                updatedFilter.amountValue2 = '';
                break;
            default:
                break;
        }

        onFilterChange(updatedFilter);
    };

    const handleSetFilterBrands = (brandId: number) => {
        const currentIndex = selectedBrands.indexOf(brandId);
        const newSelectedBrands = [...selectedBrands];

        if (currentIndex === -1) {
            newSelectedBrands.push(brandId);
        } else {
            newSelectedBrands.splice(currentIndex, 1);
        }

        setSelectedBrands(newSelectedBrands);
    };

    const handleSetFilterDateModifier = (event: SelectChangeEvent) => {
        const dateModifier: FilterTypeDate = event.target.value as FilterTypeDate;
        onFilterChange({
            ...filter,
            dateModifier: dateModifier
        });
    };

    const handleSetAmount = (event: SelectChangeEvent) => {
        const amount: FilterTypeAmount = event.target.value as FilterTypeAmount;
        onFilterChange({
            ...filter,
            amountModifier: amount
        });
    };

    const handleChangeFilterDays = (event: React.ChangeEvent<HTMLInputElement>) => {
        const days = event.target.value.replace(/\D+/g, '');
        onFilterChange({
            ...filter,
            filterDays: days
        });
    };

    const handleChangeAmountValue1 = (event: React.ChangeEvent<HTMLInputElement>) => {
        const amount1 = event.target.value.replace(/[^\d.]/g, '');
        onFilterChange({
            ...filter,
            amountValue1: amount1
        });
    };

    const handleChangeAmountValue2 = (event: React.ChangeEvent<HTMLInputElement>) => {
        const amount2 = event.target.value.replace(/[^\d.]/g, '');
        onFilterChange({
            ...filter,
            amountValue2: amount2
        });
    };

    // Returns whether the filter is active or not
    const isFilterActive = (filterName: string) => {
        switch (filterName) {
            case 'brand':
                return selectedBrands.length;
            case 'date':
                return filter.dateModifier !== 'all_time';
            case 'amount':
                return filter.amountModifier !== 'no_amount_filter';
            default:
                return false;
        }
    };

    // Returns the string associated with the filter
    const getFilterResultString = (filterObj: { name: string; title: string }) => {
        let filterResultString = null;
        const darkFont = { color: '#333' };

        switch (filterObj.name) {
            case 'brand':
                if (selectedBrands.length > 0) {
                    const firstTwoBrands = selectedBrands.slice(0, 2).map(brandId => {
                        const brand = loadedBrandsDict?.[brandId];
                        return brand?.name || 'Unknown Brand';
                    });
                    const remainingCount = selectedBrands.length - 2;
                    if (remainingCount > 0) {
                        filterResultString = (
                            <>
                                Brand: <span style={darkFont}>{firstTwoBrands.join(', ')}</span> and {remainingCount} more
                            </>
                        );
                    } else {
                        filterResultString = (
                            <>
                                Brand: <span style={darkFont}>{firstTwoBrands.join(', ')}</span>
                            </>
                        );
                    }
                } else {
                    filterResultString = <>Brand</>;
                }
                break;
            case 'date':
                const dateModifierString = convertSnakeCase(filter.dateModifier);
                const startDateString = dayjs(filter.startDate).format('MMM D, YYYY').toString();
                const endDateString = dayjs(filter.endDate).format('MMM D, YYYY').toString();
                
                if (filter.dateModifier === 'in_between') {
                    filterResultString = <>
                        Date Between: <span style={darkFont}>{startDateString} - {endDateString}</span>
                    </>;
                } else if (filter.dateModifier === 'before' || filter.dateModifier === 'after') {
                    filterResultString = <>
                        Date {dateModifierString}: <span style={darkFont}>{startDateString}</span>
                    </>;
                } else if (filter.dateModifier === 'last_days') {
                    filterResultString = <>
                        Last <span style={darkFont}>{filter.filterDays} days</span>
                    </>;
                } else if (filter.dateModifier === 'all_time') {
                    filterResultString = <>Dates</>;
                }
                break;
            case 'amount':
                const filterAmountString = convertSnakeCase(filter.amountModifier);
                
                if (filter.amountModifier === 'equal') {
                    filterResultString = <>
                        Amount <span style={darkFont}>{filterAmountString} to: ${filter.amountValue1.length ? filter.amountValue1 : '0'}</span>
                    </>;
                } else if (filter.amountModifier === 'greater_than' || filter.amountModifier === 'less_than') {
                    filterResultString = <>
                        Amount <span style={darkFont}>{filterAmountString}: ${filter.amountValue1.length ? filter.amountValue1 : '0'}</span>
                    </>;
                } else if (filter.amountModifier === 'between_two_numbers') {
                    filterResultString = <>
                        Amount Between: <span style={darkFont}>${filter.amountValue1.length ? filter.amountValue1 : '0'} - ${filter.amountValue2.length ? filter.amountValue2 : '0'}</span>
                    </>;
                } else if (filter.amountModifier === 'no_amount_filter') {
                    filterResultString = <>Amount</>;
                }
                break;
            default:
                filterResultString = null;
                break;
        }

        return filterResultString;
    };

    const renderCustomDatesPicker = () => {
        if (filter.dateModifier === 'all_time') {
            return null;
        }

        if (filter.dateModifier === 'last_days') {
            return (
                <TextField
                    label="Last X Days"
                    variant="outlined"
                    value={filter.filterDays}
                    onChange={handleChangeFilterDays}
                    style={{ marginBottom: '10px' }}
                    placeholder="Enter a number..."
                />
            );
        }

        return (
            <>
                {filter.dateModifier === 'in_between' && <p className="mr-1">From:</p>}
                <DatePickerSymphony
                    selected={dayjs(filter.startDate).toDate()}
                    onChange={(date) => {
                        if (date) {
                            onFilterChange({
                                ...filter,
                                startDate: dayjs(date).format('YYYY-MM-DD')
                            });
                        }
                    }}
                    maxDate={filter.dateModifier === 'in_between' ? dayjs(filter.endDate).subtract(1, 'day').toDate() : undefined}
                    placeholderText="Select start date"
                />
                {filter.dateModifier === 'in_between' && (
                    <>
                        <p className="mr-1">To:</p>
                        <DatePickerSymphony
                            selected={dayjs(filter.endDate).toDate()}
                            onChange={(date) => {
                                if (date) {
                                    onFilterChange({
                                        ...filter,
                                        endDate: dayjs(date).format('YYYY-MM-DD')
                                    });
                                }
                            }}
                            minDate={dayjs(filter.startDate).add(1, 'day').toDate()}
                            placeholderText="Select end date"
                        />
                    </>
                )}
            </>
        );
    };

    const renderDateFilter = () => {
        return (
            <div
                className="dateFilter"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16,
                    maxWidth: 340,
                }}
            >
                <TitlePopOver>Select Date Range</TitlePopOver>
                <FormControl fullWidth>
                    <Select
                        labelId="filter-select-label"
                        id="filter-select-date"
                        value={filter.dateModifier}
                        label="Date Filter"
                        onChange={handleSetFilterDateModifier}
                        sx={{ fontFamily: 'DIN' }}
                        IconComponent={() =>
                            <DropdownArrow
                                marginRight={10}
                                width={24}
                                height={24}
                                direction={'down'}
                            />
                        }
                    >
                        <MenuItemPopOver value="all_time">All-Time</MenuItemPopOver>
                        <MenuItemPopOver value="in_between">In-Between</MenuItemPopOver>
                        <MenuItemPopOver value="before">Before</MenuItemPopOver>
                        <MenuItemPopOver value="after">After</MenuItemPopOver>
                        <MenuItemPopOver value="last_days">Last Days</MenuItemPopOver>
                    </Select>
                </FormControl>

                <div className="flex items-center justify-center">
                    {renderCustomDatesPicker()}
                </div>
            </div>
        );
    };

    const renderAmountFilter = () => {
        return (
            <div
                className="amountFilterContainer"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16,
                    maxWidth: 340
                }}
            >
                <TitlePopOver>Filter by Amount</TitlePopOver>
                <FormControl fullWidth style={{ fontFamily: 'DIN' }}>
                    <Select
                        labelId="filter-select-label"
                        id="filter-select-amount"
                        onChange={handleSetAmount}
                        value={filter.amountModifier}
                        sx={{ fontFamily: 'DIN' }}
                        IconComponent={() =>
                            <DropdownArrow
                                marginRight={10}
                                width={24}
                                height={24}
                                direction={'down'}
                            />
                        }
                    >
                        <MenuItemPopOver value="no_amount_filter" disabled>Select an amount filter</MenuItemPopOver>
                        <MenuItemPopOver value="equal">Equal to</MenuItemPopOver>
                        <MenuItemPopOver value="greater_than">Greater than</MenuItemPopOver>
                        <MenuItemPopOver value="less_than">Less than</MenuItemPopOver>
                        <MenuItemPopOver value="between_two_numbers">Between two numbers</MenuItemPopOver>
                    </Select>
                </FormControl>

                <TextField
                    label="Amount"
                    variant="outlined"
                    value={filter.amountValue1}
                    onChange={handleChangeAmountValue1}
                    placeholder="Enter amount..."
                    style={{ fontFamily: 'DIN' }}
                    InputProps={{ style: { fontFamily: 'DIN' } }}
                />

                {filter.amountModifier === 'between_two_numbers' && (
                    <TextField
                        label="Upper Amount Limit"
                        variant="outlined"
                        value={filter.amountValue2}
                        onChange={handleChangeAmountValue2}
                        style={{ marginBottom: '10px', fontFamily: 'DIN' }}
                        InputProps={{ style: { fontFamily: 'DIN' } }}
                        placeholder="Enter upper limit..."
                    />
                )}
            </div>
        );
    };

    const renderBrandFilter = () => {
        if (!isTeamView() || !organization) return null;

        const availableBrands = organization.brands || [];
        
        const filteredBrands = availableBrands.filter((brand: CurrentBrand) =>
            brand.name?.toLowerCase().includes(brandSearchText.toLowerCase())
        );

        return (
            <div
                className="brandFilterContainer"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16,
                    maxWidth: 340,
                    maxHeight: 400
                }}
            >
                <TitlePopOver>Filter by Brand</TitlePopOver>
                
                <TextField
                    placeholder="Search brands..."
                    variant="outlined"
                    value={brandSearchText}
                    onChange={(e) => setBrandSearchText(e.target.value)}
                    style={{ marginBottom: 8 }}
                    InputProps={{ style: { fontFamily: 'DIN' } }}
                />
                
                <div
                    className="brandFilterScrollView"
                    style={{
                        maxHeight: 250,
                        overflowY: 'auto',
                        border: `1px solid ${Colors.greyLight}`,
                        borderRadius: 8,
                        backgroundColor: Colors.white
                    }}
                >
                    {filteredBrands.length === 0 ? (
                        <div style={{ 
                            padding: 16, 
                            textAlign: 'center', 
                            color: Colors.greyDark 
                        }}>
                            {brandSearchText ? `No brands found matching "${brandSearchText}"` : 'No brands available'}
                        </div>
                    ) : (
                        filteredBrands.map((brand: CurrentBrand, index: number) => {
                            const isSelected = selectedBrands.includes(brand.id);
                            return (
                                <div
                                    key={`brand-filter-${brand.id}-${index}`}
                                    className="brandFilterItem"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 12,
                                        padding: 12,
                                        cursor: 'pointer',
                                        borderBottom: `1px solid ${Colors.greyLighter}`,
                                        backgroundColor: isSelected ? Colors.purpleLight : 'transparent'
                                    }}
                                    onClick={() => handleSetFilterBrands(brand.id)}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => handleSetFilterBrands(brand.id)}
                                        style={{ marginRight: 8 }}
                                    />
                                    <CustomAvatar
                                        className="brandFilterAvatar"
                                        image={brand.image}
                                        brandOverride={brand}
                                        size={32}
                                    />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ 
                                            fontWeight: 600, 
                                            fontSize: 14,
                                            color: Colors.textColor 
                                        }}>
                                            {brand.name}
                                        </div>
                                        <div style={{ 
                                            fontSize: 12, 
                                            color: Colors.greyDark 
                                        }}>
                                            @{brand.slug}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        );
    };

    const renderPopoverContents = (filterObj: { name: string; title: string }) => {
        let body = null;
        switch (filterObj.name) {
            case 'brand':
                body = renderBrandFilter();
                break;
            case 'date':
                body = renderDateFilter();
                break;
            case 'amount':
                body = renderAmountFilter();
                break;
            default:
                body = null;
                break;
        }

        return (
            <>
                {body}
                {renderApplyButton()}
            </>
        );
    };

    const handleClosePopover = () => {
        setOpenPopoverIndex(null);
    };

    const handleClickFilterOption = (index: number) => (event: React.MouseEvent<HTMLDivElement>) => {
        setOpenPopoverIndex(openPopoverIndex === index ? null : index);
    };

    const renderApplyButton = (loading?: boolean) => {
        return (
            <>
                <ButtonSymphony
                    className="invoiceFilterApplyButton"
                    style={{ marginLeft: 'auto', marginRight: 'auto' }}
                    onClick={applyFilters}
                    disabled={loading}
                    isLoading={loading}
                    size="small"
                >
                    Apply
                </ButtonSymphony>
                <ButtonSymphony
                    className="invoiceFilterClearButton"
                    style={{ marginLeft: 'auto', marginRight: 'auto' }}
                    onClick={removeFilter}
                    variant="text"
                >
                    Clear
                </ButtonSymphony>
            </>
        );
    };

    return (
        <div
            className="invoiceFilterContainer"
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
                paddingBottom: mobileView ? 24 : 16,
            }}
        >
            <div style={{ whiteSpace: 'nowrap' }}>Filter by:</div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {(isTeamView() ? TEAM_INVOICE_FILTERS : INVOICE_FILTERS).map((filterObj, filterIndex) => {
                    const filterChipId = `${filterObj.name}-${filterIndex}`;
                    return (
                        <Fragment key={makeKey('key', filterChipId)}>
                            <Chip
                                id={filterChipId}
                                style={{
                                    backgroundColor: isFilterActive(filterObj.name) ? 'rgba(136, 0, 255, 0.08)' : '#EBEBEB',
                                    color: isFilterActive(filterObj.name) ? '#8800FF' : '#777',
                                }}
                                label={getFilterResultString(filterObj)}
                                onClick={handleClickFilterOption(filterIndex)}
                            />
                            <Popover
                                id={filterChipId}
                                open={openPopoverIndex === filterIndex}
                                anchorEl={document.getElementById(filterChipId)}
                                onClose={handleClosePopover}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                            >
                                <div
                                    className="popoverContents"
                                    style={{
                                        display: 'flex',
                                        gap: 8,
                                        padding: 16,
                                        flexDirection: 'column'
                                    }}
                                >
                                    {renderPopoverContents(filterObj)}
                                </div>
                            </Popover>
                        </Fragment>
                    );
                })}
            </div>
            <Icon
                className="invoiceFilterResetButton"
                onClick={onResetFilters}
                icon="reload"
                isLoading={isLoading}
            />
        </div>
    );
};

export default InvoiceFilterOptions;